import { useAtomValue } from 'jotai';

import {
  CentralSessionControls,
  LeftsideSessionControls,
  RightsideSessionControls,
  SessionControl,
  SessionControlsLayout,
} from '../../components/SessionControls';
import { ToolbarSeparator } from '../../components/ToolbarSeparator';
import { memoize } from '../../utils';
import { participantCountAtom } from '../../video-sdk';
import { useObscuredProviderContentProps } from '../a11y';
import { ClientsDrawerButtonWrapper as ClientsDrawerButton } from '../clients-drawer/ClientsDrawerButtonWrapper';
import { CompleteSessionButton } from '../complete-session/CompleteSessionButton';
import { RecordingControl } from '../session-recording/RecordingControl';
import { ChatDrawerControlMenuItemWrapper as ChatDrawerControlMenuItem } from './ChatDrawerControlMenuItemWrapper';
import { ClientInformationDrawerControlButtonWrapper as ClientInformationDrawerControlButton } from './ClientInformationDrawerControlButtonWrapper';
import { ClientInformationDrawerControlMenuItemWrapper as ClientInformationDrawerControlMenuItem } from './ClientInformationDrawerControlMenuItemWrapper';
import { ClientListDrawerControlMenuItemWrapper as ClientListDrawerControlMenuItem } from './ClientListDrawerControlMenuItemWrapper';
import { PatientNotePopoverControlButtonWrapper as PatientNotePopoverControlButton } from './PatientNotePopoverControlButtonWrapper';
import { ProviderAdditionalOptionsMenuWrapper as ProviderAdditionalOptionsMenu } from './ProviderAdditionalOptionsMenuWrapper';
import { ProviderAudioControlWrapper as ProviderAudioControl } from './ProviderAudioControlWrapper';
import { ProviderChatDrawerControlButtonWrapper as ProviderChatDrawerControlButton } from './ProviderChatDrawerControlButtonWrapper';
import {
  ProviderSettingsDrawerButton,
  ProviderSettingsDrawerControlMenuItemWrapper as ProviderSettingsDrawerControlMenuItem,
} from './ProviderSettingsDrawerControlsWrapper';
import { ProviderVideoControlWrapper as ProviderVideoControl } from './ProviderVideoControlWrapper';
import { SessionModeControlButtonWrapper as SessionModeControlButton } from './SessionModeControlButtonWrapper';
import { SessionModeControlMenuItemsWrapper as SessionModeControlMenuItems } from './SessionModeControlMenuItemsWrapper';

const inSessionCentralControls: SessionControl[] = [
  {
    key: 'client-list',
    additionalOptionsMenuItemComponent: ClientListDrawerControlMenuItem,
  },
  {
    key: 'session-mode',
    toolbarComponent: SessionModeControlButton,
    additionalOptionsMenuItemComponent: SessionModeControlMenuItems,
  },
  {
    key: 'client-information',
    toolbarComponent: ClientInformationDrawerControlButton,
    additionalOptionsMenuItemComponent: ClientInformationDrawerControlMenuItem,
  },
  {
    key: 'patient-note',
    toolbarComponent: PatientNotePopoverControlButton,
  },
  {
    key: 'session-tools-separator',
    toolbarComponent: ToolbarSeparator,
  },
  {
    key: 'recording',
    toolbarComponent: RecordingControl,
  },
];

const getCentralControlsMemoized = memoize((isInSession: boolean): SessionControl[] => {
  return [
    {
      key: 'audio',
      toolbarComponent: ProviderAudioControl,
    },
    {
      key: 'video',
      toolbarComponent: ProviderVideoControl,
    },
    {
      key: 'av-separator',
      toolbarComponent: ToolbarSeparator,
    },
    {
      key: 'chat',
      toolbarComponent: ProviderChatDrawerControlButton,
      additionalOptionsMenuItemComponent: ChatDrawerControlMenuItem,
    },
    ...(isInSession ? inSessionCentralControls : []),
    {
      key: 'settings',
      toolbarComponent: ProviderSettingsDrawerButton,
      additionalOptionsMenuItemComponent: ProviderSettingsDrawerControlMenuItem,
    },
  ];
});

export function ProviderSessionControlsWrapper() {
  const obscuredContentProps = useObscuredProviderContentProps();
  const isInSession = !!useAtomValue(participantCountAtom);

  return (
    <SessionControlsLayout {...obscuredContentProps} className='order-3'>
      <LeftsideSessionControls>
        <ClientsDrawerButton />
      </LeftsideSessionControls>
      <CentralSessionControls
        controls={getCentralControlsMemoized(isInSession)}
        additionalOptionsMenuComponent={ProviderAdditionalOptionsMenu}
      />
      {isInSession && (
        <RightsideSessionControls>
          <CompleteSessionButton />
        </RightsideSessionControls>
      )}
    </SessionControlsLayout>
  );
}
